import React, { useState } from "react";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import { selectConfTab, ViewList } from "../../../features/configSlice";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import { AXIOS_PUT } from "../../../config/axios";
import { PUT_CONFIG_URL } from "../../../helper/Url";
import { SetIsTriggered } from "../../../features/setLoaderSlice";
import { toast, ToastContainer } from "react-toastify";
import { selectLanguage } from "../../../features/LanguageSlice";
import { ClipLoader } from "react-spinners";
import Translate from "../../../config/Translate";
import { Base_Url } from "../../../config/variables";

const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// Extract logo path from URL and construct with Base_Url
const constructLogoUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Remove escape characters first
  let cleaned = url.replace(/\\/g, '');
  // Fix double slashes
  cleaned = cleaned.replace(/(https?:\/\/[^/]+)\/+/g, '$1/');
  cleaned = cleaned.replace(/([^:]\/)\/+/g, '$1');
  
  // Extract the path part (everything after the domain)
  try {
    const urlObj = new URL(cleaned);
    const path = urlObj.pathname; // e.g., "/storage/logos/1764574972_clinic_logo.jpeg"
    
    // Construct URL using Base_Url
    const fullUrl = `${Base_Url}${path}`;
    console.log('Original logo URL:', url);
    console.log('Extracted path:', path);
    console.log('Constructed URL with Base_Url:', fullUrl);
    return fullUrl;
  } catch (e) {
    // If URL parsing fails, try to extract path manually
    const pathMatch = cleaned.match(/(\/storage\/.*)/);
    if (pathMatch) {
      const fullUrl = `${Base_Url}${pathMatch[1]}`;
      console.log('Constructed URL (fallback):', fullUrl);
      return fullUrl;
    }
    // If no path found, return cleaned URL as fallback
    console.warn('Could not extract path from logo URL, using as-is:', cleaned);
    return cleaned;
  }
};

function EditConfig() {
  const dispatch = useDispatch();
  const getconfig = useSelector(selectConfTab).viewedit?.id?.payload;
  const [domain, setDomain] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [fm_host, setFm_host] = useState("");
  const [fm_file, setFm_file] = useState("");
  const [fm_username, setFm_username] = useState("");
  const [fm_password, setFm_password] = useState("");
  const [email_password, setEmail_password] = useState("");
  const [email_address, setEmail_address] = useState("");
  const [email_host, setEmail_host] = useState("");
  const [app_name, setApp_name] = useState("");
  const [email_encryption, setEmail_encryption] = useState("");
  const [email_port, setEmail_port] = useState("");
  const token = JSON.parse(localStorage.getItem("token"))?.access_token;
  const languageValue = useSelector(selectLanguage);
  const [Loading, setLoading] = useState(false);
  const [phone, setTelephone] = useState("");
  const [address, setAddress] = useState("");
  const [clinicLogo, setClinicLogo] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [originalLogoUrl, setOriginalLogoUrl] = useState(null); // Store original logo URL
  const [logoLoadError, setLogoLoadError] = useState(false);
  const logoBlobUrlRef = useRef(null); // Store blob URL for cleanup

  useEffect(() => {
    if (getconfig) {
      setDomain(getconfig.domain || '');
      setSubdomain(getconfig.subdomain || '');
      setFm_host(getconfig.fm_host || '');
      setFm_file(getconfig.fm_file || '');
      setFm_username(getconfig.fm_username || '');
      setFm_password(getconfig.fm_password || '');
      setEmail_password(getconfig.email_password || '');
      setEmail_address(getconfig.email_address || '');
      setEmail_host(getconfig.email_host || '');
      setApp_name(getconfig.app_name || '');
      setEmail_encryption(getconfig.email_encryption || '');
      setEmail_port(getconfig.email_port || '');
      setTelephone(getconfig.phone || '');
      setAddress(getconfig.address || '');
      
      // Construct logo URL using Base_Url
      console.log('getconfig.logo value:', getconfig.logo);
      const cleanedLogoUrl = constructLogoUrl(getconfig.logo);
      console.log('Constructed logo URL:', cleanedLogoUrl);
      if (cleanedLogoUrl) {
        setPreviewLogo(cleanedLogoUrl);
        setOriginalLogoUrl(cleanedLogoUrl);
        setLogoLoadError(false);
        
        // Try to fetch image as blob to avoid CORS issues
        fetch(cleanedLogoUrl)
          .then(response => {
            if (response.ok) {
              return response.blob();
            }
            throw new Error('Failed to fetch image');
          })
          .then(blob => {
            // Clean up previous blob URL if exists
            if (logoBlobUrlRef.current) {
              URL.revokeObjectURL(logoBlobUrlRef.current);
            }
            const objectUrl = URL.createObjectURL(blob);
            logoBlobUrlRef.current = objectUrl;
            setPreviewLogo(objectUrl);
            setLogoLoadError(false);
            console.log('Logo loaded as blob successfully');
          })
          .catch(error => {
            console.warn('Failed to fetch logo as blob, using direct URL:', error);
            setLogoLoadError(true);
            setPreviewLogo(cleanedLogoUrl); // Use direct URL as fallback
          });
      } else {
        console.warn('No logo URL found in config');
        setPreviewLogo(null);
        setOriginalLogoUrl(null);
        setLogoLoadError(false);
      }
    }
    
    // Cleanup blob URL on unmount or when config changes
    return () => {
      if (logoBlobUrlRef.current) {
        URL.revokeObjectURL(logoBlobUrlRef.current);
        logoBlobUrlRef.current = null;
      }
    };
  }, [getconfig]);

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    let data = {
      domain,
      subdomain,
      email_password,
      fm_host,
      fm_file,
      fm_username,
      fm_password,
      email_address,
      email_host,
      app_name,
      email_encryption,
      email_port,
      phone,
      address,
    };

    // Only include logo if a new image file is selected (multipart)
    if (clinicLogo) {
      // New logo file selected - convert to base64
      try {
        const base64Logo = await convertToBase64(clinicLogo);
        data.logo = base64Logo;
      } catch (error) {
        console.error('Error converting logo to base64:', error);
        setLoading(false);
        return;
      }
    }
    // If no new logo file is selected, don't include logo in data
    // This prevents overwriting the existing logo with null

    AXIOS_PUT(`${PUT_CONFIG_URL}${getconfig.id}`, { ...data }, token)
      .then((result) => {
        languageValue === "En"
          ? toast("Configuration Updated Successful!")
          : toast("Configuration mise à jour réussie !");
        setTimeout(() => {
          dispatch(SetIsTriggered());
          dispatch(ViewList());
        }, 4000);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        languageValue === "En"
          ? toast("Something Went wrong!")
          : toast("Quelque chose s'est mal passé!");
      });
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setClinicLogo(file);
      setPreviewLogo(URL.createObjectURL(file));
    }
  };

  return (
    <div className="edit_config">
      <ToastContainer />
      <h4>
        Edit Configuration{" "}
        <button className="closebtn" onClick={() => dispatch(ViewList())}>
          <CloseIcon />
        </button>
      </h4>
      <form onSubmit={handleSubmitEdit}>
        <div className="group">
          <div className="single">
            <label htmlFor=""><Translate Word="page.Domain" /></label>
            <input
              type="text"
              required
              defaultValue={getconfig?.domain}
              name="domain"
              onChange={(e) => setDomain(e.target.value)}
            />
          </div>
          <div className="single">
            <label htmlFor=""><Translate Word="page.Subdomain" /></label>
            <input
              type="text"
              required
              name="subdomain"
              onChange={(e) => setSubdomain(e.target.value)}
              defaultValue={getconfig?.subdomain}
            />
          </div>{" "}
          <div className="single">
            <label htmlFor=""><Translate Word="page.FilemakerHost" /></label>
            <input
              type="text"
              required
              name="fm_host"
              onChange={(e) => setFm_host(e.target.value)}
              defaultValue={getconfig?.fm_host}
            />
          </div>{" "}
        </div>
        <div className="group">
          <div className="single">
            <label htmlFor=""><Translate Word="page.FilemakerFile" /></label>
            <input
              type="text"
              required
              name="fm_file"
              onChange={(e) => setFm_file(e.target.value)}
              defaultValue={getconfig?.fm_file}
            />
          </div>
          <div className="single">
            <label htmlFor=""><Translate Word="page.FilemakerUsername" /></label>
            <input
              type="text"
              required
              name="fm_username"
              onChange={(e) => setFm_username(e.target.value)}
              defaultValue={getconfig?.fm_username}
            />
          </div>{" "}
          <div className="single">
            <label htmlFor=""><Translate Word="page.FilemakerPassword" /></label>
            <input
              type="text"
              required
              name="fm_password"
              onChange={(e) => setFm_password(e.target.value)}
              defaultValue={getconfig?.fm_password}
            />
          </div>{" "}
        </div>
        <div className="group">
          <div className="single">
            <label htmlFor=""><Translate Word="page.EmailPassword" /></label>
            <input
              type="text"
              required
              name="email_password"
              onChange={(e) => setEmail_password(e.target.value)}
              defaultValue={getconfig?.email_password}
            />
          </div>
          <div className="single">
            <label htmlFor=""><Translate Word="page.EmailAddress" /></label>
            <input
              type="email"
              required
              name="email_address"
              onChange={(e) => setEmail_address(e.target.value)}
              defaultValue={getconfig?.email_address}
            />
          </div>{" "}
          <div className="single">
            <label htmlFor=""><Translate Word="page.EmailHost" /></label>
            <input
              type="text"
              required
              name="email_host"
              onChange={(e) => setEmail_host(e.target.value)}
              defaultValue={getconfig?.email_host}
            />
          </div>{" "}
        </div>
        <div className="group">
          <div className="single">
            <label htmlFor=""><Translate Word="page.AppName" /></label>
            <input
              type="text"
              required
              name="app_name"
              onChange={(e) => setApp_name(e.target.value)}
              defaultValue={getconfig?.app_name}
            />
          </div>
          <div className="single">
            <label htmlFor=""><Translate Word="page.EmailEncryption" /></label>
            <input
              type="text"
              required
              name="email_encryption"
              onChange={(e) => setEmail_encryption(e.target.value)}
              defaultValue={getconfig?.email_encryption}
            />
          </div>{" "}
          <div className="single">
            <label htmlFor=""><Translate Word="page.EmailPort" /></label>
            <input
              type="text"
              required
              name="email_port"
              onChange={(e) => setEmail_port(e.target.value)}
              defaultValue={getconfig?.email_port}
            />
          </div>{" "}
        </div>
        <div className="group">
          <div className="single">
            <label htmlFor=""><Translate Word="sidebar.telephone" />  </label>
            <input
              type="text"
              required
              name="telephone"
              onChange={(e) => setTelephone(e.target.value)}
              defaultValue={getconfig?.phone}
            />
          </div>
          <div className="single">
            <label htmlFor=""><Translate Word="sidebar.address" /></label>
            <input
              type="text"
              required
              name="address"
              onChange={(e) => setAddress(e.target.value)}
              defaultValue={getconfig?.address}
            />
          </div>
          <div className="single">
            <label htmlFor=""><Translate Word="page.ClinicLogo" /></label>
            {(previewLogo || originalLogoUrl) && (
              <div className="logo-preview" style={{ marginBottom: '10px' }}>
                {logoLoadError || !previewLogo ? (
                  // Show link if image failed to load
                  <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '15px',
                    backgroundColor: '#f9f9f9',
                    textAlign: 'center'
                  }}>
                    <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                      {languageValue === "En" 
                        ? "Image preview unavailable" 
                        : "Aperçu de l'image indisponible"}
                    </p>
                    <a 
                      href={originalLogoUrl || previewLogo} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#1976d2', 
                        textDecoration: 'underline',
                        fontSize: '14px'
                      }}
                    >
                      {languageValue === "En" 
                        ? "Click to view logo in new tab" 
                        : "Cliquez pour voir le logo dans un nouvel onglet"}
                    </a>
                  </div>
                ) : (
                  // Show image if loaded successfully
                  <div style={{ 
                    border: '1px solid #ddd', 
                    borderRadius: '4px', 
                    padding: '10px',
                    backgroundColor: '#f9f9f9',
                    display: 'inline-block'
                  }}>
                    <img 
                      src={previewLogo} 
                      alt="Clinic Logo" 
                      style={{ 
                        maxWidth: '150px', 
                        maxHeight: '150px',
                        display: 'block',
                        marginBottom: '5px'
                      }} 
                      onLoad={() => {
                        console.log('Logo image loaded successfully');
                        setLogoLoadError(false);
                      }}
                      onError={(e) => {
                        console.error('Error loading logo image. URL:', previewLogo);
                        setLogoLoadError(true);
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                  {languageValue === "En" 
                    ? "Current logo (select new file to replace)" 
                    : "Logo actuel (sélectionnez un nouveau fichier pour remplacer)"}
                </p>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
            />
          </div>
        </div>
        <div className="action">
          <button>
            {" "}
            {Loading ? (
              <ClipLoader size={20} color="rgb(121 173 192)" />
            ) : (
              "save"
            )}
          </button>
          <button
            type="button"
            className="cancel"
            onClick={() => dispatch(ViewList())}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditConfig;

export const Container = styled.div`
  height: 100vh;
`;
